import { isAxiosError, type AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hook_au/useAuth';
import type { Category, CategoryCreatePayload, CategoryUpdatePayload } from '../types/categories';
import apiClient from './apiClient';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

type CategoryFilters = {
  search?: string;
  active?: boolean;
  limit?: number;
};

const ERROR_MESSAGES = {
  unauthorized: 'La sesión expiró. Vuelve a iniciar sesión para continuar.',
  notFound: 'No encontramos la categoría solicitada.',
  conflict: 'No pudimos completar la operación por un conflicto de datos.',
  unknown: 'No pudimos conectar con el servicio de categorías. Inténtalo de nuevo en unos segundos.',
} as const;

export function useCategoriesApi(initialFilters: CategoryFilters = { active: true, limit: 200 }) {
  const { token } = useAuth();
  const initialFiltersRef = useRef(initialFilters);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de categorías');
      }
      const response = await apiClient.request<Response>({
        ...config,
        url: `/categories${config.url ?? ''}`,
      });
      return response.data;
    },
    [token],
  );

  const fetchCategories = useCallback(
    async (overrides: CategoryFilters = {}) => {
      setLoading(true);
      setError(null);
      try {
        const filters = {
          ...initialFiltersRef.current,
          ...overrides,
          search: (overrides.search ?? initialFiltersRef.current.search ?? '').trim() || undefined,
        };
        const data = await request<Category[]>({
          method: 'GET',
          url: '',
          params: filters,
        });
        setCategories(data);
        return data;
      } catch (err) {
        const message =
          isAxiosError(err) && err.response?.status === 401
            ? 'Sesión caducada o sin permisos. Inicia sesión nuevamente.'
            : 'No pudimos cargar las categorías. Inténtalo de nuevo en unos segundos.';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [request],
  );

  useEffect(() => {
    if (!token) return;
    void fetchCategories();
  }, [token, fetchCategories]);

  return { categories, loading, error, refresh: fetchCategories };
}

export function useCategoryActions() {
  const { token } = useAuth();

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de categorías');
      }

      try {
        const response = await apiClient.request<Response>({
          ...config,
          url: `/categories${config.url ?? ''}`,
        });
        return response.data;
      } catch (err) {
        if (isAxiosError(err)) {
          const status = err.response?.status;
          const detail =
            typeof err.response?.data === 'object' &&
            err.response?.data &&
            'detail' in err.response.data &&
            typeof err.response.data.detail === 'string'
              ? err.response.data.detail
              : undefined;
          if (status === 401) throw new Error(ERROR_MESSAGES.unauthorized);
          if (status === 404) throw new Error(ERROR_MESSAGES.notFound);
          if (status === 409) throw new Error(detail ?? ERROR_MESSAGES.conflict);
        }
        throw new Error(ERROR_MESSAGES.unknown);
      }
    },
    [token],
  );

  const createCategory = useCallback(
    (payload: CategoryCreatePayload) =>
      request<Category>({
        method: 'POST',
        url: '',
        data: payload,
      }),
    [request],
  );

  const updateCategory = useCallback(
    (id: Category['id'], payload: CategoryUpdatePayload) =>
      request<Category>({
        method: 'PUT',
        url: `/${id}`,
        data: payload,
      }),
    [request],
  );

  const deleteCategory = useCallback(
    (id: Category['id']) =>
      request<void>({
        method: 'DELETE',
        url: `/${id}`,
      }),
    [request],
  );

  return { createCategory, updateCategory, deleteCategory };
}
